import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const defaultFields = {
  name: "",
  mobile: "",
  email: "",
  city: "",
  vehicle_type: "",
  num_vehicles: "",
  message: "",
};

export default function EnquiryForm({ enquiry_type = "general", product_id = null, title = "Get a Free Quote", subtitle = "", cta = "Send Enquiry", compact = false, testIdPrefix = "enquiry" }) {
  const [form, setForm] = useState(defaultFields);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Name and mobile are required");
      return;
    }
    if (!/^\d{10}$/.test(form.mobile.trim())) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", {
        ...form,
        num_vehicles: form.num_vehicles ? parseInt(form.num_vehicles) : null,
        enquiry_type,
        product_id,
      });
      toast.success("Thanks! Our team will reach out shortly.");
      setForm(defaultFields);
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className={`${compact ? "" : "bg-white p-6 sm:p-8 rounded-2xl border border-slate-200"}`} data-testid={`${testIdPrefix}-form`}>
      {!compact && (
        <div className="mb-6">
          <div className="eyebrow">Talk to an expert</div>
          <h3 className="font-heading text-2xl font-bold text-[#0A192F] mt-2">{title}</h3>
          {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Full Name*</label>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid={`${testIdPrefix}-name`}
            className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#FF6D00] focus:ring-2 focus:ring-[#FF6D00]/20 outline-none" />
        </div>
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Mobile*</label>
          <input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} required inputMode="numeric" maxLength={10} data-testid={`${testIdPrefix}-mobile`}
            className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#FF6D00] focus:ring-2 focus:ring-[#FF6D00]/20 outline-none" />
        </div>
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</label>
          <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid={`${testIdPrefix}-email`}
            className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#FF6D00] focus:ring-2 focus:ring-[#FF6D00]/20 outline-none" />
        </div>
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">City</label>
          <input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid={`${testIdPrefix}-city`}
            className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#FF6D00] focus:ring-2 focus:ring-[#FF6D00]/20 outline-none" />
        </div>
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Vehicle Type</label>
          <select value={form.vehicle_type} onChange={(e) => set("vehicle_type", e.target.value)} data-testid={`${testIdPrefix}-vehicle`}
            className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#FF6D00] outline-none bg-white">
            <option value="">Select</option>
            <option>Car / SUV</option>
            <option>Two-Wheeler</option>
            <option>Truck / Commercial</option>
            <option>School Bus</option>
            <option>Fleet (multiple)</option>
          </select>
        </div>
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">No. of Vehicles</label>
          <input type="number" min={1} value={form.num_vehicles} onChange={(e) => set("num_vehicles", e.target.value)} data-testid={`${testIdPrefix}-numvehicles`}
            className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#FF6D00] outline-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Requirement / Message</label>
          <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} data-testid={`${testIdPrefix}-message`}
            className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#FF6D00] outline-none resize-none" />
        </div>
      </div>
      <button type="submit" disabled={loading} data-testid={`${testIdPrefix}-submit`}
        className="mt-5 inline-flex items-center justify-center h-12 px-8 rounded-full bg-[#FF6D00] text-white font-semibold hover:bg-[#ff8433] active:scale-95 transition-transform disabled:opacity-50">
        {loading ? "Sending..." : cta}
      </button>
    </form>
  );
}
